/**
 * Barre de progression segmentée du déploiement (§3.3).
 *
 * Un segment par étape **attendue pour ce type de job** (`pipelineStepsFor`),
 * de largeur proportionnelle à l'amplitude de sa plage dans `PROGRESS_RANGES` :
 * la géométrie est calculée par `lib/progress.ts`, ce composant ne fait que la
 * peindre. Un rollback affiche donc deux segments, et non six dont quatre
 * resteraient vides jusqu'au bout.
 */

import { STATE_LABELS, type DeploymentState, type DeploymentType } from '@portail/shared';
import clsx from 'clsx';
import { Check, Circle, RefreshCw, X } from 'lucide-react';

import { buildSegments, type SegmentStatus } from '../lib/progress';

/** Habillage du remplissage selon l'état du segment. */
const FILL_CLASSES: Record<SegmentStatus, string> = {
  done: 'bg-emerald-500',
  current: 'bg-indigo-500',
  failed: 'bg-red-500',
  pending: 'bg-transparent',
};

/** Icône de la liste d'étapes. */
function StepIcon({ status }: { status: SegmentStatus }) {
  if (status === 'done') {
    return <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />;
  }
  if (status === 'failed') {
    return <X className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden="true" />;
  }
  if (status === 'current') {
    return (
      <RefreshCw
        className="h-4 w-4 animate-spin text-indigo-600 dark:text-indigo-400"
        aria-hidden="true"
      />
    );
  }
  return <Circle className="h-4 w-4 text-slate-300 dark:text-slate-700" aria-hidden="true" />;
}

/** Propriétés de la barre. */
export interface ProgressBarProps {
  state: DeploymentState;
  /** Progression affichée (valeur animée), entre 0 et 100. */
  progress: number;
  failedStep: DeploymentState | null;
  /** Message d'étape publié par le pipeline, affiché sous le libellé. */
  message?: string | null;
  /** Nature du job (§4.2) : un rollback n'a que deux étapes. */
  type?: DeploymentType;
}

/** Barre segmentée + liste des étapes. */
export function ProgressBar({
  state,
  progress,
  failedStep,
  message,
  type = 'DEPLOY',
}: ProgressBarProps) {
  const segments = buildSegments({ state, progress, failedStep, type });
  const rounded = Math.round(progress);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-base font-medium text-slate-900 dark:text-slate-100">
            {STATE_LABELS[state]}
          </p>
          {message ? (
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">{message}</p>
          ) : null}
        </div>
        <p
          className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100"
          aria-hidden="true"
        >
          {rounded} %
        </p>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={rounded}
        aria-valuetext={`${rounded} % — ${STATE_LABELS[state]}`}
        className="flex h-3 w-full gap-px overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
      >
        {segments.map((segment) => (
          <div
            key={segment.step}
            style={{ width: `${segment.widthPercent}%` }}
            className="relative h-full overflow-hidden bg-slate-200 dark:bg-slate-800"
            title={`${segment.label} (${segment.start}–${segment.end} %)`}
          >
            <div
              className={clsx(
                'h-full transition-[width] duration-150 ease-linear',
                FILL_CLASSES[segment.status],
              )}
              style={{ width: `${segment.fill * 100}%` }}
            />
            {segment.status === 'current' ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/20"
              />
            ) : null}
          </div>
        ))}
      </div>

      <ol className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
        {segments.map((segment) => (
          <li key={segment.step} className="flex items-center gap-2 text-sm">
            <StepIcon status={segment.status} />
            <span
              className={clsx(
                'truncate',
                segment.status === 'pending' && 'text-slate-400 dark:text-slate-600',
                segment.status === 'current' && 'font-medium text-slate-900 dark:text-slate-100',
                segment.status === 'done' && 'text-slate-600 dark:text-slate-400',
                segment.status === 'failed' && 'font-medium text-red-700 dark:text-red-400',
              )}
              title={segment.label}
            >
              {segment.label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
